/**
 * Brand colours: the accent colour staff pick for light and dark mode, checked
 * for contrast, and the CSS the web app adds for it. Shared so the settings form, the panel and the
 * web app use the same rule.
 */

export const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/** The page backgrounds an accent is shown on (the lighter/darker of each theme's page and card). */
export const ACCENT_BACKGROUNDS = Object.freeze({ light: '#eef3f0', dark: '#1f1c2c' });
/** Text colours put on an accent button: whichever reads better. */
export const ACCENT_TEXT = Object.freeze({ light: '#ffffff', dark: '#15131f' });
/** WCAG AA for normal text. */
export const MIN_CONTRAST = 4.5;

function channel(v) {
  const c = v / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Relative luminance (WCAG 2) of a #rrggbb colour. */
export function luminance(hex) {
  const [r, g, b] = [1, 3, 5].map((i) => channel(parseInt(hex.slice(i, i + 2), 16)));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Contrast ratio of two #rrggbb colours, 1 to 21. */
export function contrastRatio(a, b) {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** The text colour (light or dark) that reads best on `hex`. */
export function textOn(hex) {
  return contrastRatio(hex, ACCENT_TEXT.light) >= contrastRatio(hex, ACCENT_TEXT.dark)
    ? ACCENT_TEXT.light
    : ACCENT_TEXT.dark;
}

/**
 * Problems with an accent for a theme ('light' or 'dark'): it must stand out from that theme's
 * background (links and outlines use it as text colour) and carry readable button text.
 */
export function accentProblems(hex, theme) {
  if (!HEX_COLOR.test(hex)) return ['Use a colour like #c2367a.'];
  const problems = [];
  const bg = contrastRatio(hex, ACCENT_BACKGROUNDS[theme]);
  if (bg < MIN_CONTRAST) {
    problems.push(
      `Contrast with the ${theme} background is ${bg.toFixed(2)}:1; it needs at least ${MIN_CONTRAST}:1. Pick a ${theme === 'light' ? 'darker' : 'lighter'} colour.`,
    );
  }
  const text = contrastRatio(hex, textOn(hex));
  if (text < MIN_CONTRAST) {
    problems.push(`Button text on this colour reaches only ${text.toFixed(2)}:1; it needs ${MIN_CONTRAST}:1.`);
  }
  return problems;
}

/**
 * CSS for the chosen accents ('' keeps the built-in one): the same variables as the web app's
 * styles.css, for light, dark-by-system and dark-by-choice. Values are checked hex colours.
 */
export function accentCss({ light = '', dark = '' } = {}) {
  const vars = (hex, mix) =>
    `--accent:${hex};--accent-hover:color-mix(in srgb, ${hex} 85%, ${mix});--accent-text:${textOn(hex)};--focus:${hex};`;
  const out = [];
  if (HEX_COLOR.test(light)) out.push(`:root{${vars(light, 'black')}}`);
  if (HEX_COLOR.test(dark)) {
    const d = vars(dark, 'white');
    out.push(`@media (prefers-color-scheme: dark){:root:not([data-theme='light']){${d}}}`);
    out.push(`:root[data-theme='dark']{${d}}`);
  }
  return out.join('\n');
}
